<template>
  <main ref="newsroomPage" class="newsroom-page">
    <section ref="latestSection" class="latest-news latest-news-reveal" aria-labelledby="latest-news-title">
      <div class="newsroom-container">
        <h1 id="latest-news-title" class="latest-news__heading">{{ t('news.latest') }}</h1>

        <div v-if="topLoading" class="newsroom-section-state" role="status" aria-live="polite">
          <span class="newsroom-spinner" aria-hidden="true"></span>
          <p>{{ t('news.loadingLatest') }}</p>
        </div>

        <EmptyState
          v-else-if="topError"
          class="newsroom-section-state newsroom-section-state--message"
          icon="error"
          :title="t('news.latestUnavailable')"
          :description="topError"
          :show-action="true"
          :action-text="t('news.reload')"
          @action="fetchTopNews"
        />

        <template v-else-if="primaryArticle">
          <article class="latest-primary latest-news__card latest-news__card--primary">
            <a
              class="news-media latest-primary__media"
              :href="getDetailHref(primaryArticle)"
              :aria-label="`${t('news.readArticle')} ${primaryArticle.title}`"
              @click.prevent="viewDetail(primaryArticle)"
            >
              <img
                v-if="hasUsableImage(primaryArticle)"
                class="news-media__image"
                :src="getImagePath(primaryArticle.coverImage)"
                :alt="primaryArticle.title"
                @load="markMediaReady"
                @error="handleImageError(primaryArticle, $event)"
              >
              <span v-else class="news-image-placeholder" aria-hidden="true">
                <img src="/static/images/newsroom-v2/image-placeholder.png" alt="">
              </span>
            </a>

            <div class="latest-primary__content">
              <h2>
                <a :href="getDetailHref(primaryArticle)" @click.prevent="viewDetail(primaryArticle)">
                  {{ primaryArticle.title }}
                </a>
              </h2>
              <p class="news-meta">{{ formatMeta(primaryArticle) }}</p>
              <p class="news-summary latest-primary__summary">{{ getSummary(primaryArticle) }}</p>
              <a class="news-readmore" :href="getDetailHref(primaryArticle)" @click.prevent="viewDetail(primaryArticle)">
                {{ t('actions.continueReading') }}
              </a>
            </div>
          </article>

          <div v-if="secondaryArticles.length" class="latest-secondary-grid">
            <article v-for="item in secondaryArticles" :key="item.id" class="latest-secondary latest-news__card">
              <a
                class="news-media latest-secondary__media"
                :href="getDetailHref(item)"
                :aria-label="`${t('news.readArticle')} ${item.title}`"
                @click.prevent="viewDetail(item)"
              >
                <img
                  v-if="hasUsableImage(item)"
                  class="news-media__image"
                  :src="getImagePath(item.coverImage)"
                  :alt="item.title"
                  @load="markMediaReady"
                  @error="handleImageError(item, $event)"
                >
                <span v-else class="news-image-placeholder" aria-hidden="true">
                  <img src="/static/images/newsroom-v2/image-placeholder.png" alt="">
                </span>
              </a>
              <h2>
                <a :href="getDetailHref(item)" @click.prevent="viewDetail(item)">{{ item.title }}</a>
              </h2>
              <p class="news-meta">{{ formatMeta(item) }}</p>
              <a class="news-readmore" :href="getDetailHref(item)" @click.prevent="viewDetail(item)">
                {{ t('actions.continueReading') }}
              </a>
            </article>
          </div>
        </template>

        <div v-else class="newsroom-section-state newsroom-section-state--empty">
          <p>{{ t('news.noTop') }}</p>
        </div>
      </div>
    </section>

    <section ref="moreSection" class="more-news more-news-reveal" aria-labelledby="more-news-title">
      <div class="newsroom-container">
        <h2 id="more-news-title">{{ t('news.more') }}</h2>

        <div v-if="moreLoading" class="newsroom-section-state" role="status" aria-live="polite">
          <span class="newsroom-spinner" aria-hidden="true"></span>
          <p>{{ t('news.loadingMore') }}</p>
        </div>

        <EmptyState
          v-else-if="moreError"
          class="newsroom-section-state newsroom-section-state--message"
          icon="error"
          :title="t('news.moreUnavailable')"
          :description="moreError"
          :show-action="true"
          :action-text="t('news.reload')"
          @action="fetchMoreNews"
        />

        <div v-else-if="moreArticles.length" class="more-news-list">
          <article v-for="item in moreArticles" :key="item.id" class="more-news-card">
            <a
              class="news-media more-news-card__media"
              :href="getDetailHref(item)"
              :aria-label="`${t('news.readArticle')} ${item.title}`"
              @click.prevent="viewDetail(item)"
            >
              <img
                v-if="hasUsableImage(item)"
                class="news-media__image"
                :src="getImagePath(item.coverImage)"
                :alt="item.title"
                loading="lazy"
                decoding="async"
                @load="markMediaReady"
                @error="handleImageError(item, $event)"
              >
              <span v-else class="news-image-placeholder" aria-hidden="true">
                <img src="/static/images/newsroom-v2/image-placeholder.png" alt="">
              </span>
            </a>

            <div class="more-news-card__content">
              <h3>
                <a :href="getDetailHref(item)" @click.prevent="viewDetail(item)">{{ item.title }}</a>
              </h3>
              <p class="news-meta">{{ formatMeta(item) }}</p>
              <p class="news-summary more-news-card__summary">{{ getSummary(item) }}</p>
              <a class="news-readmore" :href="getDetailHref(item)" @click.prevent="viewDetail(item)">
                {{ t('actions.continueReading') }}
              </a>
            </div>
          </article>
        </div>

        <div v-else class="newsroom-section-state newsroom-section-state--empty">
          <p>{{ t('news.noMore') }}</p>
        </div>

        <router-link class="view-more-button" :to="allNewsPath">{{ t('news.viewMore') }}</router-link>
      </div>
    </section>
  </main>
</template>

<script>
import EmptyState from '@/components/EmptyState.vue'
import { newsAPI } from '@/utils/api'
import { imgPath } from '@/utils/main'
import {
  buildAllNewsPath,
  buildNewsDetailPath,
  normalizeNewsPageResponse,
  shouldUseNewsPlaceholder
} from '@/utils/news-list.cjs'

const TOP_NEWS_TYPE = 'TOP'
const MORE_NEWS_REQUEST_SIZE = 8
const MORE_NEWS_DISPLAY_LIMIT = 5
const { siteLanguageMixin } = require('@/utils/site-language.js')
const { createLatestSiteRequestGuard } = require('@/utils/site-request.cjs')

export default {
  name: 'News',
  components: { EmptyState },
  mixins: [siteLanguageMixin],
  data() {
    return {
      topArticles: [],
      normalArticles: [],
      topLoading: false,
      moreLoading: false,
      topError: null,
      moreError: null,
      failedImages: {},
      topRequestGuard: createLatestSiteRequestGuard(),
      moreRequestGuard: createLatestSiteRequestGuard(),
      pageRevealObserver: null,
      latestInitialObservationPending: true,
      latestInitialRevealTimer: null
    }
  },
  computed: {
    primaryArticle() {
      return this.topArticles[0] || null
    },
    secondaryArticles() {
      return this.topArticles.slice(1, 3)
    },
    moreArticles() {
      const topIds = new Set(this.topArticles.map(item => String(item.id)))
      return this.normalArticles
        .filter(item => !topIds.has(String(item.id)))
        .slice(0, MORE_NEWS_DISPLAY_LIMIT)
    },
    allNewsPath() {
      return buildAllNewsPath()
    }
  },
  async mounted() {
    document.title = 'Newsroom - ANOVATOR'
    await this.refreshNews()
  },
  beforeUnmount() {
    if (this.pageRevealObserver) this.pageRevealObserver.disconnect()
    if (this.latestInitialRevealTimer !== null) window.clearTimeout(this.latestInitialRevealTimer)
  },
  watch: {
    currentLanguage() {
      this.refreshNews()
    }
  },
  methods: {
    initPageReveal() {
      const page = this.$refs.newsroomPage
      if (!page || window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof window.IntersectionObserver !== 'function') return
      const isMobile = window.matchMedia('(max-width: 700px)').matches
      const targets = [this.$refs.latestSection, this.$refs.moreSection]
      const observableTargets = targets.filter(Boolean)
      if (!observableTargets.length || this.pageRevealObserver) return

      try {
        this.pageRevealObserver = new window.IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target === this.$refs.latestSection && !isMobile && this.latestInitialObservationPending) {
              this.latestInitialObservationPending = false
              if (entry.isIntersecting) {
                this.scheduleLatestInitialReveal(entry.target)
                return
              }
            }
            if (!entry.isIntersecting && entry.boundingClientRect.top >= 0) return
            entry.target.classList.add('is-revealed')
            this.pageRevealObserver.unobserve(entry.target)
          })
        }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' })
        observableTargets.forEach((target) => this.pageRevealObserver.observe(target))
        page.classList.add('reveal-enabled')
      } catch (error) {
        if (this.pageRevealObserver) this.pageRevealObserver.disconnect()
        this.pageRevealObserver = null
      }
    },
    async refreshNews() {
      await Promise.all([this.fetchTopNews(), this.fetchMoreNews()])
      this.$nextTick(this.initPageReveal)
    },
    scheduleLatestInitialReveal(target) {
      if (this.latestInitialRevealTimer !== null) return
      this.latestInitialRevealTimer = window.setTimeout(() => {
        target.classList.add('is-revealed')
        if (this.pageRevealObserver) this.pageRevealObserver.unobserve(target)
        this.latestInitialRevealTimer = null
      }, 150)
    },
    async fetchTopNews() {
      const request = this.topRequestGuard.start(this.currentLanguage)
      this.topLoading = true
      this.topError = null
      try {
        const response = await newsAPI.getFeaturedNews(TOP_NEWS_TYPE, request.siteType)
        if (!this.topRequestGuard.isCurrent(request, this.currentLanguage)) return
        if (!response || response.code !== 0 || !Array.isArray(response.data)) {
          throw new Error((response && response.message) || 'The TOP news service returned an invalid response.')
        }
        this.topArticles = response.data.slice(0, 3)
      } catch (requestError) {
        if (!this.topRequestGuard.isCurrent(request, this.currentLanguage)) return
        console.error('获取置顶新闻失败:', requestError)
        this.topError = this.t('news.networkTopError')
        this.topArticles = []
      } finally {
        if (this.topRequestGuard.isCurrent(request, this.currentLanguage)) this.topLoading = false
      }
    },
    async fetchMoreNews() {
      const request = this.moreRequestGuard.start(this.currentLanguage)
      this.moreLoading = true
      this.moreError = null
      try {
        const response = normalizeNewsPageResponse(await newsAPI.getNewsList({
          pageIndex: 1,
          pageSize: MORE_NEWS_REQUEST_SIZE
        }, request.siteType))
        if (!this.moreRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.normalArticles = response.list
      } catch (requestError) {
        if (!this.moreRequestGuard.isCurrent(request, this.currentLanguage)) return
        console.error('获取普通新闻失败:', requestError)
        this.moreError = this.t('news.networkMoreError')
        this.normalArticles = []
      } finally {
        if (this.moreRequestGuard.isCurrent(request, this.currentLanguage)) this.moreLoading = false
      }
    },
    getDetailHref(item) {
      return buildNewsDetailPath(item && item.urlSlug)
    },
    viewDetail(item) {
      if (!item || !item.urlSlug) return
      const url = this.$router.resolve(buildNewsDetailPath(item.urlSlug)).href
      window.open(url, '_blank', 'noopener')
    },
    getImagePath(image) {
      return imgPath(image)
    },
    getImageKey(item) {
      return String(item.id || item.coverImage || item.title)
    },
    hasUsableImage(item) {
      return !shouldUseNewsPlaceholder(item.coverImage, this.failedImages[this.getImageKey(item)])
    },
    markMediaReady(event) {
      const media = event && (event.currentTarget || event.target)
      if (media && media.classList) media.classList.add('is-media-ready')
    },
    handleImageError(item, event) {
      this.markMediaReady(event)
      this.failedImages[this.getImageKey(item)] = true
    },
    getSummary(item) {
      return item.summary || ''
    },
    formatMeta(item) {
      return this.formatDate(item.publishTime || item.gmtCreate)
    },
    formatDate(value) {
      if (!value) return ''
      const date = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString(this.currentLanguage === 'ZH' ? 'zh-CN' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    }
  }
}
</script>

<style scoped>
.newsroom-page,
.newsroom-page * { box-sizing: border-box; }

.newsroom-page {
  min-width: 0;
  min-height: 100vh;
  padding-top: var(--global-header-height, 44px);
  overflow: hidden;
  color: #000;
  background: #fff;
  font-family: MiSans, Arial, sans-serif;
}

.newsroom-container {
  width: min(calc(100vw - 144px), 1296px);
  margin-right: auto;
  margin-left: max(72px, calc((100vw - 1296px) / 2));
}

.latest-news {
  padding: 68px 0 80px;
  background: #fff;
}

.latest-news h1,
.more-news > .newsroom-container > h2 {
  margin: 0;
  color: #000;
  font-size: 40px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.latest-primary {
  display: grid;
  grid-template-columns: minmax(0, 618fr) minmax(0, 631fr);
  gap: 47px;
  margin-top: 29px;
}

.news-media {
  position: relative;
  display: block;
  overflow: hidden;
  background: #f5f5f7;
}

.news-media > img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.news-media > img:not(.news-media__image) { transition: transform 0.35s ease; }

.news-media__image {
  opacity: 0;
  transition: opacity 240ms ease, transform 0.35s ease;
}

.news-media__image.is-media-ready { opacity: 1; }

.news-media:hover > img,
.news-media:focus-visible > img { transform: scale(1.02); }

.news-media:focus-visible,
.latest-primary h2 a:focus-visible,
.latest-secondary h2 a:focus-visible,
.more-news-card h3 a:focus-visible,
.news-readmore:focus-visible,
.view-more-button:focus-visible {
  outline: 2px solid #ef0a6a;
  outline-offset: 3px;
}

.latest-primary__media {
  width: 100%;
  height: 347px;
}

.latest-secondary__media {
  width: 100%;
  aspect-ratio: 618 / 347;
}

.news-image-placeholder {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  background: #f5f5f7;
}

.news-image-placeholder img {
  display: block;
  width: 54px;
  height: 45px;
  opacity: 0.2;
}

.latest-primary__content,
.more-news-card__content {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.latest-primary h2,
.latest-secondary h2,
.more-news-card h3 {
  min-width: 0;
  max-width: 100%;
  margin: 0;
  font-size: 30px;
  font-weight: 700;
  line-height: 1.22;
  letter-spacing: -0.02em;
}

.latest-primary h2 a,
.latest-secondary h2 a,
.more-news-card h3 a {
  display: block;
  width: 100%;
  max-width: 100%;
  color: #000;
  text-decoration: none;
  transition: color 0.2s ease;
  overflow-wrap: anywhere;
  word-break: normal;
}

.latest-primary h2 a:hover,
.latest-secondary h2 a:hover,
.more-news-card h3 a:hover { color: #ef0a6a; }

.news-meta,
.news-summary { margin: 0; }

.news-meta {
  margin-top: 24px;
  color: rgba(0, 0, 0, 0.5);
  font-size: 12px;
  line-height: 1.4;
}

.news-summary {
  display: -webkit-box;
  overflow: hidden;
  margin-top: 20px;
  color: rgba(0, 0, 0, 0.8);
  font-size: 13px;
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
}

.latest-primary__summary {
  margin-top: 30px;
  -webkit-line-clamp: 6;
}

.news-readmore {
  align-self: flex-start;
  margin-top: auto;
  padding-top: 18px;
  color: #ef0a6a;
  font-size: 12px;
  line-height: 1.4;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.news-readmore:hover { color: #c90051; }

.latest-secondary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 618px));
  gap: 47px;
  margin-top: 53px;
}

.latest-secondary {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.latest-secondary h2 { margin-top: 24px; }
.latest-secondary .news-meta { margin-top: 18px; }

@media (min-width: 701px) {
  .newsroom-page.reveal-enabled .latest-news__heading,
  .newsroom-page.reveal-enabled .latest-news-reveal .latest-news__card {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 720ms ease-out, transform 720ms ease-out;
  }

  .newsroom-page.reveal-enabled .latest-news-reveal:not(.is-revealed) .latest-news__heading,
  .newsroom-page.reveal-enabled .latest-news-reveal:not(.is-revealed) .latest-news__card {
    opacity: 0;
    transform: translateY(42px);
  }

  .newsroom-page.reveal-enabled .more-news-reveal {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 620ms ease-out, transform 620ms ease-out;
  }

  .newsroom-page.reveal-enabled .more-news-reveal:not(.is-revealed) {
    opacity: 0;
    transform: translateY(30px);
  }

  .newsroom-page.reveal-enabled .latest-news-reveal .latest-news__card--primary {
    transition-delay: 120ms;
  }

  .newsroom-page.reveal-enabled .latest-news-reveal .latest-news__heading {
    transition-delay: 0;
  }

  .newsroom-page.reveal-enabled .latest-news-reveal .latest-secondary:nth-child(1) {
    transition-delay: 240ms;
  }

  .newsroom-page.reveal-enabled .latest-news-reveal .latest-secondary:nth-child(2) {
    transition-delay: 360ms;
  }
}

.more-news {
  padding: 80px 0 120px;
  background: #fff;
}

.more-news-list {
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin-top: 41px;
}

.more-news-card {
  display: grid;
  grid-template-columns: 408px minmax(0, 1fr);
  gap: 30px;
}

.more-news-card__media {
  width: 408px;
  height: 230px;
}

.more-news-card h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
}

.more-news-card .news-meta { margin-top: 9px; }

.more-news-card__summary {
  margin-top: 20px;
  -webkit-line-clamp: 5;
}

.view-more-button {
  display: flex;
  width: max-content;
  min-width: 112px;
  height: 50px;
  align-items: center;
  justify-content: center;
  margin: 60px auto 0;
  padding: 0 25px;
  border: 1px solid #000;
  border-radius: 95px;
  color: #000;
  background: transparent;
  font-size: 14px;
  text-decoration: none;
  transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}

.view-more-button:hover,
.view-more-button:focus-visible {
  border-color: #ef0a6a;
  color: #fff;
  background: #ef0a6a;
}

.newsroom-section-state {
  display: flex;
  min-height: 230px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 41px;
  color: #000;
  background: #f5f5f7;
}

.newsroom-section-state p {
  margin: 18px 0 0;
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
}

.newsroom-section-state--message { padding: 32px 16px; }
.newsroom-section-state--empty p { margin-top: 0; }

.newsroom-spinner {
  width: 34px;
  height: 34px;
  border: 2px solid rgba(0, 0, 0, 0.16);
  border-top-color: #ef0a6a;
  border-radius: 50%;
  animation: newsroom-spin 0.8s linear infinite;
}

@keyframes newsroom-spin { to { transform: rotate(360deg); } }

@media (min-width: 901px) and (max-width: 1399px) {
  .latest-news { padding: 32px 0 44px; }
  .latest-news h1 { font-size: 32px; line-height: 1.3; }

  .latest-primary {
    gap: 30px;
    margin-top: 20px;
  }

  .latest-primary__media { height: 230px; }

  .latest-primary h2,
  .latest-secondary h2 {
    font-size: 24px;
    line-height: 1.25;
  }

  .latest-primary .news-meta { margin-top: 14px; }
  .latest-primary__summary {
    margin-top: 16px;
    -webkit-line-clamp: 3;
  }

  .latest-primary .news-readmore,
  .latest-secondary .news-readmore { padding-top: 12px; }

  .latest-secondary-grid {
    gap: 30px;
    margin-top: 32px;
  }

  .latest-secondary__media {
    height: 225px;
    aspect-ratio: auto;
  }
  .latest-secondary h2 { margin-top: 16px; }
  .latest-secondary .news-meta { margin-top: 12px; }
}

@media (min-width: 701px) and (max-width: 1399px) {
  .latest-news { padding-bottom: 40px; }
  .more-news { padding-top: 40px; }
}

@media (max-width: 900px) {
  .newsroom-container {
    width: calc(100vw - 48px);
    margin-left: 24px;
  }
  .latest-primary,
  .latest-secondary-grid { gap: 30px; }
  .latest-secondary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .more-news-card { grid-template-columns: minmax(280px, 408px) minmax(0, 1fr); }
  .more-news-card__media { width: 100%; }
}

@media (max-width: 700px) {
  .newsroom-page.reveal-enabled .latest-news-reveal,
  .newsroom-page.reveal-enabled .more-news-reveal {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 560ms ease-out, transform 560ms ease-out;
  }

  .newsroom-page.reveal-enabled .latest-news-reveal:not(.is-revealed),
  .newsroom-page.reveal-enabled .more-news-reveal:not(.is-revealed) {
    opacity: 0;
    transform: translateY(20px);
  }

  .newsroom-page { padding-top: var(--global-header-height, 50px); }
  .newsroom-container {
    width: calc(100vw - 32px);
    margin-left: 16px;
  }
  .latest-news,
  .more-news { padding: 56px 0; }
  .latest-news h1,
  .more-news > .newsroom-container > h2 { font-size: 40px; }

  .latest-primary {
    grid-template-columns: 1fr;
    gap: 26px;
    margin-top: 32px;
  }

  .latest-primary__media {
    height: auto;
    aspect-ratio: 400 / 224;
  }

  .latest-primary h2,
  .latest-secondary h2 { font-size: 30px; }
  .latest-primary__summary { margin-top: 20px; -webkit-line-clamp: 5; }
  .latest-primary .news-readmore { margin-top: 18px; }

  .latest-secondary-grid {
    grid-template-columns: 1fr;
    gap: 44px;
    margin-top: 52px;
  }

  .latest-secondary h2 { margin-top: 20px; }
  .latest-secondary .news-readmore { margin-top: 18px; }
  .more-news { border-top: 1px solid #eee; }
  .more-news-list { gap: 48px; margin-top: 32px; }

  .more-news-card {
    grid-template-columns: 1fr;
    gap: 22px;
  }

  .more-news-card__media {
    width: 100%;
    height: auto;
    aspect-ratio: 400 / 230;
  }

  .more-news-card__summary { -webkit-line-clamp: 4; }
  .more-news-card .news-readmore { margin-top: 18px; }
  .view-more-button { margin-top: 48px; }
  .newsroom-section-state { min-height: 180px; margin-top: 32px; }
}

@media (prefers-reduced-motion: reduce) {
  .newsroom-page.reveal-enabled .latest-news-reveal,
  .newsroom-page.reveal-enabled .latest-news__heading,
  .newsroom-page.reveal-enabled .latest-news-reveal .latest-news__card,
  .newsroom-page.reveal-enabled .more-news-reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }

  .news-media > img,
  .view-more-button { transition: none; }
  .news-media__image {
    opacity: 1;
    transition: none;
  }
  .newsroom-spinner { animation-duration: 1.6s; }
}
</style>
